import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Dismissible banner shown at the top of every dashboard until the user
 * verifies their email. One-click "Resend verification email".
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(true);

  useEffect(() => {
    if (!user) return;
    // Trust the user object first — if email_verified is explicitly true, never show.
    if (user.email_verified === true) {
      setVerified(true);
      return;
    }
    // Otherwise fetch authoritative status (handles refresh after verification).
    api.get("/auth/verification-status").then((r) => setVerified(!!r.data.email_verified)).catch(() => {});
  }, [user]);

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/send-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error("Could not send verification email.");
    } finally {
      setBusy(false);
    }
  };

  if (!user || verified || hidden) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200" data-testid="verify-email-banner">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3 text-[13px]">
        <div className="flex items-center gap-2 text-amber-900 min-w-0">
          <Mail className="size-4 shrink-0" />
          <span className="truncate">
            Verify <b>{user.email}</b> to receive invoices, payout receipts and account alerts.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={resend}
            disabled={busy}
            className="font-medium text-amber-900 underline-offset-2 hover:underline disabled:opacity-50"
            data-testid="verify-email-resend"
          >
            {busy ? "Sending…" : "Resend email"}
          </button>
          <button
            onClick={() => setHidden(true)}
            className="text-amber-700 hover:text-amber-900 rounded-full p-1 hover:bg-amber-100"
            aria-label="Dismiss"
            data-testid="verify-email-dismiss"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
